import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/use-auth.js';
import { redeemPendingInvite } from '../auth/invite-redemption.js';
import { StudioSetupForm } from '../studios/studio-setup-form.js';
import { AuthShell } from './auth-shell.js';

export function WelcomePage(): JSX.Element {
  const { refreshMemberships, setActiveStudioId, signOut } = useAuth();
  const navigate = useNavigate();

  // Always probe for a pending invite before showing the studio-setup form,
  // so a user who landed here via the invite flow doesn't flash the form on
  // their way to the inviting studio. Catches every path to /welcome,
  // including the cross-browser email-confirm flow that bypasses the redeem
  // step in /auth/callback. The redemption util itself short-circuits when
  // there is nothing to redeem (no localStorage stash AND no metadata
  // token), so the form appears almost immediately for non-invite users.
  const [phase, setPhase] = useState<'checking' | 'form'>('checking');

  useEffect(() => {
    void (async () => {
      const result = await redeemPendingInvite();
      if (result !== null) {
        // Pin the inviting studio as active BEFORE refreshing memberships, so
        // any route that re-renders mid-refresh (RequireAuthNoMembership ->
        // RootRedirect) picks it up rather than falling back to studios[0]
        // (which can be the just-created personal studio).
        setActiveStudioId(result.id);
        await refreshMemberships();
        navigate(`/s/${result.shortId}`, { replace: true });
        return;
      }
      setPhase('form');
    })();
  }, [navigate, refreshMemberships, setActiveStudioId]);

  if (phase === 'checking') {
    return (
      <AuthShell
        title="Getting things ready…"
        subtitle="One moment while we set up your account."
      >
        <p className="text-sm text-text-muted">Almost there.</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Set up your studio"
      subtitle="A few quick details so we can tailor your experience."
    >
      <div className="flex flex-col gap-6">
        <StudioSetupForm
          initialName="My Studio"
          submitLabel="Continue"
          submittingLabel="Creating studio…"
          onCreated={async () => {
            await refreshMemberships();
            navigate('/', { replace: true });
          }}
        />

        <p className="text-center text-sm text-text-muted">
          Not the right account?{' '}
          <button
            type="button"
            onClick={() => { void signOut(); }}
            className="text-accent underline underline-offset-2 hover:opacity-80 transition-opacity"
          >
            Sign out
          </button>
        </p>
      </div>
    </AuthShell>
  );
}
